import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { Button } from "@vertesia/ui/core";
import { useUITranslation } from "@vertesia/ui/i18n";
import { MarkdownRenderer } from "@vertesia/ui/widgets";
import { MessageSquare, CheckCircle, XCircle, AlertCircle, HelpCircle, Send } from "lucide-react";
const VARIANT_STYLES = {
    default: {
        border: "border-l-attention",
        bg: "bg-amber-50 dark:bg-amber-900/20",
        icon: "text-attention",
    },
    warning: {
        border: "border-l-destructive",
        bg: "bg-red-50 dark:bg-red-900/20",
        icon: "text-destructive",
    },
    info: {
        border: "border-l-info",
        bg: "bg-blue-50 dark:bg-blue-900/20",
        icon: "text-info",
    },
    success: {
        border: "border-l-success",
        bg: "bg-green-50 dark:bg-green-900/20",
        icon: "text-success",
    },
};
const VARIANT_ICONS = {
    default: HelpCircle,
    warning: AlertCircle,
    info: MessageSquare,
    success: CheckCircle,
};
/**
 * AskUserWidget - A styled component for displaying agent prompts/questions to users
 *
 * Use this when the agent needs user input, confirmation, or selection from options.
 * Supports both option selection and free-form text input.
 */
export function AskUserWidget({ question, description, options, onSelect, onMultiSelect, onSubmit, allowFreeResponse = false, multiSelect = false, placeholder, isLoading = false, icon, variant = "default", hideIcon = false, hideBorder = false, 
// Styling props
className, cardClassName, headerClassName, iconClassName, questionClassName, descriptionClassName, optionsClassName, buttonClassName, inputContainerClassName, inputClassName, submitButtonClassName, }) {
    const { t } = useUITranslation();
    const resolvedPlaceholder = placeholder ?? t('agent.typeYourResponse');
    const [inputValue, setInputValue] = React.useState("");
    const [selectedOptions, setSelectedOptions] = React.useState(new Set());
    const inputRef = React.useRef(null);
    const styles = VARIANT_STYLES[variant];
    const DefaultIcon = VARIANT_ICONS[variant];
    const toggleOption = (optionId) => {
        setSelectedOptions((prev) => {
            const next = new Set(prev);
            if (next.has(optionId)) {
                next.delete(optionId);
            }
            else {
                next.add(optionId);
            }
            return next;
        });
    };
    const handleMultiSubmit = () => {
        if (selectedOptions.size > 0 && onMultiSelect) {
            onMultiSelect(Array.from(selectedOptions));
            setSelectedOptions(new Set());
        }
    };
    const handleSubmit = () => {
        if (inputValue.trim() && onSubmit) {
            onSubmit(inputValue.trim());
            setInputValue("");
        }
    };
    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        }
    };
    const borderClass = hideBorder ? "" : `border-l-4 ${styles.border}`;
    return (_jsx("div", { className: `my-4 font-sans ${className || ""}`, children: _jsxs("div", { className: `${borderClass} ${styles.bg} rounded-r-lg shadow-sm ${cardClassName || ""}`, children: [_jsx("div", { className: `px-4 py-3 ${headerClassName || ""}`, children: _jsxs("div", { className: "flex items-start gap-3", children: [!hideIcon && (_jsx("div", { className: `flex-shrink-0 mt-0.5 ${styles.icon} ${iconClassName || ""}`, children: icon || _jsx(DefaultIcon, { className: "size-5" }) })), _jsxs("div", { className: "flex-1 min-w-0", children: [_jsx("div", { className: `prose prose-sm dark:prose-invert max-w-none text-gray-900 dark:text-gray-100 ${questionClassName || ""}`, children: _jsx(MarkdownRenderer, { children: question }) }), description && (_jsx("p", { className: `mt-1 text-sm text-gray-600 dark:text-gray-400 ${descriptionClassName || ""}`, children: description }))] })] }) }), options && options.length > 0 && (_jsx("div", { className: `px-4 pb-3 pt-1 ${optionsClassName || ""}`, children: multiSelect ? (_jsxs("div", { className: "space-y-2", children: [options.map((option) => (_jsxs("label", { className: `flex items-start gap-3 px-4 py-3 rounded-lg cursor-pointer transition-colors
                                            ${selectedOptions.has(option.id)
                                    ? "bg-blue-50 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-700"
                                    : "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"}
                                            ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`, children: [_jsx("input", { type: "checkbox", checked: selectedOptions.has(option.id), onChange: () => toggleOption(option.id), disabled: isLoading, className: "mt-0.5 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700" }), _jsxs("div", { className: "flex-1 overflow-hidden", children: [_jsxs("div", { className: "flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100 break-words", children: [option.icon, _jsx("span", { className: "break-words", children: option.label })] }), option.description && (_jsx("div", { className: "mt-1 text-sm text-gray-500 dark:text-gray-400 break-words whitespace-pre-wrap", children: option.description }))] })] }, option.id))), _jsx("div", { className: "pt-2", children: _jsxs(Button, { size: "sm", onClick: handleMultiSubmit, disabled: isLoading || selectedOptions.size === 0, className: "flex items-center gap-2", children: [_jsx(Send, { className: "size-4" }), selectedOptions.size > 0 ? t('agent.submitSelectionCount', { count: selectedOptions.size }) : t('agent.submitSelection')] }) })] })) : (_jsx("div", { className: "flex flex-col gap-2 w-full", children: options.map((option) => (_jsx("button", { onClick: () => onSelect?.(option.id), disabled: isLoading, className: `w-full text-left px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700
                                            bg-white dark:bg-gray-800
                                            hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-600
                                            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1
                                            transition-colors
                                            ${isLoading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
                                            ${buttonClassName || ""}`, children: _jsxs("div", { className: "flex items-start gap-3", children: [option.icon && (_jsx("span", { className: "flex-shrink-0 mt-0.5 text-gray-500 dark:text-gray-400", children: option.icon })), _jsxs("div", { className: "flex-1 overflow-hidden", children: [_jsx("div", { className: "font-medium text-sm text-gray-900 dark:text-gray-100 break-words", children: option.label }), option.description && (_jsx("div", { className: "mt-1 text-sm text-gray-500 dark:text-gray-400 break-words whitespace-pre-wrap", children: option.description }))] })] }) }, option.id))) })) })), allowFreeResponse && (_jsx("div", { className: `px-4 pb-3 pt-1 ${inputContainerClassName || ""}`, children: _jsxs("div", { className: "flex gap-2", children: [_jsx("input", { ref: inputRef, type: "text", value: inputValue, onChange: (e) => setInputValue(e.target.value), onKeyDown: handleKeyDown, placeholder: resolvedPlaceholder, disabled: isLoading, className: `flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${inputClassName || ""}` }), _jsx(Button, { size: "sm", onClick: handleSubmit, disabled: isLoading || !inputValue.trim(), className: submitButtonClassName, children: isLoading ? "..." : t('agent.send') })] }) }))] }) }));
}
export function ConfirmationWidget({ question, description, onConfirm, onCancel, confirmLabel, cancelLabel, isLoading = false, variant = "default", className, }) {
    const { t } = useUITranslation();
    const resolvedConfirmLabel = confirmLabel ?? t('agent.yes');
    const resolvedCancelLabel = cancelLabel ?? t('agent.no');
    return (_jsx(AskUserWidget, { question: question, description: description, variant: variant, isLoading: isLoading, className: className, options: [
            {
                id: "confirm",
                label: resolvedConfirmLabel,
                icon: _jsx(CheckCircle, { className: "size-4" }),
            },
            {
                id: "cancel",
                label: resolvedCancelLabel,
                icon: _jsx(XCircle, { className: "size-4" }),
            },
        ], onSelect: (id) => {
            if (id === "confirm")
                onConfirm();
            else
                onCancel();
        } }));
}
//# sourceMappingURL=AskUserWidget.js.map